import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiUrlServices } from '@app/interfaces';
import { IProcessStatus } from '@app/interfaces/process.interface';
import { OnboardingCloudEnum } from '@app/modules/shared/components/onboarding-permissions-tree/onboarding-cloud-type.enum';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { AzureOnboardingService } from '@modules/onboarding/service/azure-onboarding/azure-onboarding.service';
import { PartnerAliasEnum } from '@partners/partner-alias.enum';
import { PartnersEnum } from '@partners/partners.enum';
import { getApiUrl, getRoleUrlTemplate, IRoleUrlTemplateParams } from '@utils/helpers';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import { catchError, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { IPartnerStatusResp } from '../../../partner-portal/interfaces/partner-portal.interface';
import {
	notReady1,
	notReady3,
	ReadyAll,
} from '../../../partner-portal/services/partner-onboarding-service/partner-onboarding.service';
import {
	IAWSJSONPreview,
	IOnboardingAccount,
	IOnboardingTask,
	PermissionNode,
} from '../../interfaces/onboarding.interface';
import { OnboardingService } from '../onboarding.service';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';

@Injectable({
	providedIn: 'root',
})
export class AwsOnboardingService {
	public domain: string = this.appConfig.config.apiUrl;
	public selectedPermissions: PermissionNode[];
	public stopPolling$ = new Subject();
	public isReonboarding = false;
	public curBucketName: string;
	public curType: 'cur_v1' | 'cur_v2';
	public jsonTemplate: string;
	public permissionsTree: any;
	public currentUUID = '';
	public currentARN = '';
	public accountId = ''; // used when updating acc permissions

	private _previewAWSJson$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
	private _accountsIdMapping: Map<string, string> = new Map<string, string>();

	constructor(
		public featureFlagManagerService: FeatureFlagManagerService,
		private azureOnboardingService: AzureOnboardingService,
		private onboardingService: OnboardingService,
		private appConfig: ConfigFacadeService,
		private http: HttpClient,
	) {}

	public setAccountIdMapping(accountId: string, accountUUID: string): void {
		this._accountsIdMapping.set(accountId, accountUUID);
	}

	public getAWSJSONPreview(): Observable<IAWSJSONPreview> {
		return this._previewAWSJson$.asObservable();
	}

	public getJsonPreview(params): Observable<IAWSJSONPreview> {
		const url = getApiUrl(this.domain) + `/frontend/account/generate-onboarding-json-preview`;
		return this.http.post<IAWSJSONPreview>(url, params).pipe(
			tap((response) => {
				this._previewAWSJson$.next(response);
			}),
		);
	}

	public getPermissionsMap(
		label: string,
		cloud: OnboardingCloudEnum,
	): Observable<{ permissions_map: any; template: any }> {
		const url = getApiUrl(this.domain, ApiUrlServices.account) + '/get-template-permissions';

		// specific condition for the whitelabels which name starts with a digit
		if (label === PartnersEnum['2bcloud']) {
			label = PartnerAliasEnum.TwoBCloud;
		}

		if (cloud === OnboardingCloudEnum.Azure) {
			return this.azureOnboardingService.getPermissionMap();
		}

		return this.http.post<{ permissions_map: any; template: any }>(url, { label, cloud });
	}

	public getAccountTK(account_id: string, is_reconnecting = true): Observable<{ tk: string }> {
		if (this._accountsIdMapping.has(account_id)) {
			return of({
				tk: this._accountsIdMapping.get(account_id),
			});
		}

		const url = getApiUrl(this.domain, ApiUrlServices.account) + '/get-account-tk';
		return this.http.post<{ tk: string }>(url, { account_id, is_reconnecting });
	}

	public getAccountPermissions(uuid: string): Observable<{ permissions: any }> {
		const url = getApiUrl(this.domain, ApiUrlServices.account) + '/get-account-permissions';
		return this.http.post<{ permissions: any }>(url, { uuid });
	}

	public getAccountFeaturesList(uuid: string): Observable<{ features_list: string[] }> {
		const url = getApiUrl(this.domain, ApiUrlServices.customer) + '/get_features_list/' + uuid;
		return this.http.get<{ features_list: string[] }>(url);
	}

	public generateJsonUrl(params): Observable<{ url: string }> {
		const url = getApiUrl(this.domain, ApiUrlServices.account) + '/generate-onboarding-json';
		return this.http.post<{ url: string }>(url, params);
	}

	/**
	 * Checks arn for single account onboarding, validates arn and creates onboarding task
	 * @param params.
	 */
	public validateArnWithTask(params): Observable<any> {
		const { arn, uuid, reonboarding, bucket, type } = params;
		const url = getApiUrl(this.domain, ApiUrlServices.onboard) + '/validate_arn';
		return this.http.post<any>(url, {
			arn,
			account_uuid: uuid,
			cur_bucket_name: bucket || '',
			optionalManagement: true,
			optionalS3: true,
			readonlyPerm: false,
			reonboarding,
			cur_version: type,
		});
	}

	/**
	 * Checks arn for multiple accounts onboarding, only validates arn and does not create task
	 * @param arn
	 * @param uuid
	 * @param bucket
	 */
	public validateArnWithoutTask(arn, uuid, bucket: string): Observable<any> {
		const url = getApiUrl(this.domain, ApiUrlServices.onboard) + '/is_valid_arn';
		return this.http.post<any>(url, {
			arn,
			account_uuid: uuid,
			cur_bucket_name: bucket,
		});
	}

	public checkValidateStatus(account_uuid: string): Observable<any> {
		const url = getApiUrl(this.domain, ApiUrlServices.onboard) + '/validate_status';
		return this.http.post<any>(url, { account_uuid });
	}

	public formAWSLink(linkParams): string {
		const { region, uuidToken, permission, templateUrl, curBucket } = linkParams;
		const today = new Date();
		const dd = today.getDate();

		const mm = today.getMonth() + 1;
		const yyyy = today.getFullYear();

		const dateSuffix = '-' + mm + '-' + dd + '-' + yyyy;
		const params: IRoleUrlTemplateParams = {
			productLabelPrefix: this.appConfig.config.partner.prefix,
			subject: uuidToken,
			region,
			permission,
			createDate: dateSuffix,
			templateUrl,
			curBucket,
		};
		return getRoleUrlTemplate(params);
	}

	public getDashboardReadiness(uuid: string): Observable<any> {
		const url = getApiUrl(this.domain) + `/frontend/customer/is_alive/${uuid}`;
		return this.http.get<any>(url);
	}

	/**
	 * Creates onboarding task for multiple accounts
	 * @param {IOnboardingAccount[]} accounts
	 */
	public onboardMultipleAccounts(accounts: IOnboardingAccount[]): Observable<IOnboardingTask> {
		const url = getApiUrl(this.domain, ApiUrlServices.onboard) + '/onboard-multiple-accounts';
		return this.http.post<IOnboardingTask>(url, { data: accounts });
	}

	/**
	 * Check onboarding task status
	 * @param taskId
	 */
	public checkOnboardingStatus(taskId: string): Observable<any> {
		const url = getApiUrl(this.domain, ApiUrlServices.onboard) + '/poll-onboarding-status';
		return this.http.post(url, { task_id: taskId });
	}

	/**
	 * Polling for onboarding task completion
	 * @param {string} taskId
	 */
	public startOnboardingPolling(taskId: string): void {
		this.onboardingService.process = IProcessStatus.loading;
		timer(6000, 6000)
			.pipe(
				mergeMap((_) => this.checkOnboardingStatus(taskId)),
				// mergeMap(_ => this.simulateOnboarding(requestCount)),
				takeUntil(this.stopPolling$),
				catchError((err) => {
					this.stopPolling$.next();
					this.onboardingService.onboardingInProgress$.next(false);
					this.onboardingService.clearProgress();
					this.onboardingService.process = IProcessStatus.failed;
					return of({} as IPartnerStatusResp);
				}),
			)
			.subscribe({
				next: (resp) => {
					// requestCount++;
					switch (resp.message) {
						case 'Ready':
							this.stopPolling$.next();
							this.mapOnboardingStatus(resp);
							this.onboardingService.calculateProgress(resp.task);
							this.onboardingService.onboardingSuccess$.next(true);
							this.onboardingService.onboardingSuccess();
							break;
						case 'Task not found':
							this.stopPolling$.next();
							this.onboardingService.onboardingInProgress$.next(false);
							break;
						case 'Not ready yet':
							this.onboardingService.calculateProgress(resp.task);
					}
				},
			});
	}

	public simulateOnboarding(count): Observable<IPartnerStatusResp> {
		switch (count) {
			case 0:
				return of(notReady1);
			case 1:
				return of(notReady3);
			case 2:
				return of(ReadyAll);
			default:
				return of({} as IPartnerStatusResp);
		}
	}

	/**
	 * Map onboarding status from resp to queue
	 * @param resp
	 * @private
	 */
	private mapOnboardingStatus(resp) {
		const queue = this.onboardingService.onboardingQueue$.value;
		queue.forEach((account) => {
			const accountsMap = resp.task.accounts;
			const accountUuid = Object.keys(accountsMap).find((uuid) => uuid === account.account_uuid);
			const accountToOnboard = accountsMap[accountUuid];
			account.onboarding_status = accountToOnboard.onboarding_status;
		});

		this.onboardingService.onboardingQueue$.next(queue);
	}
}
